import { useState } from 'react';
import { Link } from 'react-router-dom';
import { format, formatDistanceToNow, isPast } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Building2, Clock, Pencil, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  TASK_PRIORITY_CLASSES,
  TASK_PRIORITY_LABELS,
  TASK_SOURCE_LABELS,
  type TaskWithClient,
} from './types';
import { useCompleteTask, useDeleteTask, useReopenTask } from './useTasks';
import { TaskModal } from './TaskModal';

interface Props {
  task: TaskWithClient;
  compact?: boolean;
}

export function TaskCard({ task, compact = false }: Props) {
  const complete = useCompleteTask();
  const reopen = useReopenTask();
  const remove = useDeleteTask();
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isCompleted = task.status === 'completed';
  const isOverdue = !isCompleted && task.due_at !== null && isPast(new Date(task.due_at));

  const handleToggle = (checked: boolean | string) => {
    if (checked) {
      void complete.mutateAsync(task.id);
    } else {
      void reopen.mutateAsync(task.id);
    }
  };

  return (
    <>
      <div
        className={`group flex items-start gap-3 rounded-lg border bg-white p-3 transition-shadow hover:shadow-sm ${
          isOverdue ? 'border-expense/30' : 'border-border'
        } ${isCompleted ? 'opacity-60' : ''}`}
      >
        <Checkbox
          checked={isCompleted}
          onCheckedChange={handleToggle}
          disabled={complete.isPending || reopen.isPending}
          aria-label={isCompleted ? 'Reabrir tarefa' : 'Concluir tarefa'}
          className="mt-1"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`text-sm font-medium ${isCompleted ? 'text-muted-foreground line-through' : 'text-foreground'}`}
            >
              {task.title}
            </span>
            {task.priority !== 'normal' && (
              <Badge
                variant="outline"
                className={`text-[10px] ${TASK_PRIORITY_CLASSES[task.priority]}`}
              >
                {TASK_PRIORITY_LABELS[task.priority]}
              </Badge>
            )}
            {task.source !== 'manual' && (
              <Badge variant="outline" className="text-[10px]">
                {TASK_SOURCE_LABELS[task.source]}
              </Badge>
            )}
          </div>
          {!compact && task.description && (
            <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{task.description}</p>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            {task.due_at && (
              <span className={`inline-flex items-center gap-1 ${isOverdue ? 'text-expense' : ''}`}>
                <Clock size={11} />
                {isOverdue
                  ? `Atrasada ${formatDistanceToNow(new Date(task.due_at), { locale: ptBR })}`
                  : format(new Date(task.due_at), "dd/MM 'às' HH:mm", { locale: ptBR })}
              </span>
            )}
            {task.client_id && task.client_name && (
              <Link
                to={`/app/clientes`}
                className="inline-flex items-center gap-1 hover:text-primary hover:underline"
              >
                <Building2 size={11} />
                {task.client_name}
              </Link>
            )}
          </div>
        </div>
        <div className="flex shrink-0 gap-0.5 opacity-0 group-hover:opacity-100">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setEditOpen(true)}
            title="Editar"
          >
            <Pencil size={12} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            onClick={() => setConfirmDelete(true)}
            title="Excluir"
          >
            <Trash2 size={12} />
          </Button>
        </div>
      </div>

      <TaskModal open={editOpen} onOpenChange={setEditOpen} edit={task} />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir tarefa?</AlertDialogTitle>
            <AlertDialogDescription>
              "{task.title}" será removida. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => remove.mutate(task.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
